import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faUsers,
  faBed,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";

export const occupancyForBooking = (b) =>
  b.is_guest_only ? (b.guest_count || 0) : 1 + (b.guest_count || 0);

export const AvailableCell = ({ price, configName, selected, isPast, onMouseDown }) => (
  <div
    className={`rooms-info-cell rooms-info-cell--available${selected ? " rooms-info-cell--drag-selected" : ""}${
      isPast ? " rooms-info-cell--bookable-past" : ""
    }`}
    onMouseDown={onMouseDown}
    style={{ cursor: "pointer", userSelect: "none" }}
  >
    <span className="rooms-info-cell-price">{price}</span>
    {configName && <span className="rooms-info-cell-config-name">{configName}</span>}
  </div>
);

export const DisabledCell = () => (
  <div className="rooms-info-cell rooms-info-cell--disabled" />
);

export const PastEmptyCell = ({ price, configName }) => (
  <div className="rooms-info-cell rooms-info-cell--available rooms-info-cell--past">
    <span className="rooms-info-cell-price">{price}</span>
    {configName && <span className="rooms-info-cell-config-name">{configName}</span>}
  </div>
);

const BookedCellInner = ({
  booking,
  cellDefaultPrice,
  totalOccupantsInCell,
  cellBedCount,
  showOccupantDetails,
  onToggleOccupantDetails,
}) => {
  const person = booking.participant_id;
  const name = person
    ? `${person.name || ""} ${person.lname || ""}`.trim()
    : "(Unknown)";
  const bookingOccupants = occupancyForBooking(booking);
  const bookingParticipants = booking.is_guest_only ? 0 : 1;
  const bookingGuests = booking.guest_count || 0;
  const occupantsLabel =
    bookingParticipants > 0 || bookingGuests > 0
      ? `(${bookingParticipants} participant${bookingParticipants !== 1 ? "s" : ""}, ${bookingGuests} extra${bookingGuests !== 1 ? "s" : ""})`
      : "";

  return (
    <>
      <div className="rooms-info-cell-top">
        <span className="rooms-info-cell-name">
          {name}
          {booking.is_guest_only && (
            <span className="rooms-info-guest-only-badge">GO</span>
          )}
        </span>
        <span className="rooms-info-cell-price-booked">
          ${booking.price != null ? booking.price : cellDefaultPrice}
        </span>
      </div>
      <div className="rooms-info-cell-bottom">
        <span className="rooms-info-cell-meta">
          <span className="rooms-info-cell-meta-line">
            <FontAwesomeIcon icon={faUsers} className="rooms-info-icon" />
            Guests: {bookingOccupants}
            {(bookingParticipants > 0 || bookingGuests > 0) && (
              <button
                type="button"
                className="rooms-info-cell-info-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleOccupantDetails();
                }}
                title="Guest details"
                aria-label="Toggle guest details"
              >
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className="rooms-info-icon rooms-info-icon-info-icon"
                />
              </button>
            )}
          </span>
          {showOccupantDetails && occupantsLabel ? (
            <span className="rooms-info-cell-meta-occupants rooms-info-cell-meta-occupants--expanded">
              {occupantsLabel}
            </span>
          ) : null}
        </span>
        <span className="rooms-info-cell-beds">
          <FontAwesomeIcon icon={faBed} className="rooms-info-icon" />
          Beds: {totalOccupantsInCell}/{cellBedCount}
        </span>
      </div>
    </>
  );
};

export const BookedCell = ({
  cellBookings,
  bedCount: cellBedCount,
  defaultPrice: cellDefaultPrice,
  isPast,
  editSelected,
}) => {
  const [idx, setIdx] = useState(0);
  const [prevIdx, setPrevIdx] = useState(0);
  const [slideDir, setSlideDir] = useState(null);
  const [showOccupantDetails, setShowOccupantDetails] = useState(false);
  const count = cellBookings.length;
  const safeIdx = idx % count;

  const goNext = (e) => {
    e.stopPropagation();
    setPrevIdx(safeIdx);
    setSlideDir("forward");
    setIdx((prev) => (prev + 1) % count);
  };
  const goPrev = (e) => {
    e.stopPropagation();
    setPrevIdx(safeIdx);
    setSlideDir("backward");
    setIdx((prev) => (prev - 1 + count) % count);
  };

  useEffect(() => {
    if (slideDir == null) return;
    const t = setTimeout(() => setSlideDir(null), 260);
    return () => clearTimeout(t);
  }, [slideDir, idx]);

  const b = cellBookings[safeIdx];
  const totalOccupantsInCell = cellBookings.reduce((s, x) => s + occupancyForBooking(x), 0);

  const variantClass =
    b.allocation_type === "private"
      ? "rooms-info-cell--private"
      : "rooms-info-cell--standard";

  const pastClass = isPast ? " rooms-info-cell--past" : "";

  const isTransitioning = slideDir != null;
  const outgoingBooking = cellBookings[prevIdx % count];

  const incomingSlideClass =
    slideDir === "forward"
      ? " rooms-info-cell-slide-in-right"
      : slideDir === "backward"
        ? " rooms-info-cell-slide-in-left"
        : "";

  // Forward (next) click: incoming enters from the right, so outgoing exits left.
  // Backward (prev) click: incoming enters from the left, so outgoing exits right.
  const outgoingSlideClass =
    slideDir === "forward"
      ? "rooms-info-cell-slide-out-left"
      : "rooms-info-cell-slide-out-right";

  return (
    <div
      className={`rooms-info-cell ${variantClass}${pastClass}${editSelected ? " rooms-info-cell--edit-drag-selected" : ""}`}
      style={{
        cursor: "pointer",
        overflow: "hidden",
        position: "relative",
        userSelect: "none",
      }}
    >
      {isTransitioning ? (
        <>
          <div
            key={`out-${prevIdx}`}
            className={`rooms-info-cell-inner ${outgoingSlideClass}`}
            aria-hidden="true"
            inert=""
          >
            <BookedCellInner
              booking={outgoingBooking}
              cellDefaultPrice={cellDefaultPrice}
              totalOccupantsInCell={totalOccupantsInCell}
              cellBedCount={cellBedCount}
              showOccupantDetails={showOccupantDetails}
              onToggleOccupantDetails={() => setShowOccupantDetails((prev) => !prev)}
            />
          </div>
          <div key={`in-${idx}`} className={`rooms-info-cell-inner${incomingSlideClass}`}>
            <BookedCellInner
              booking={b}
              cellDefaultPrice={cellDefaultPrice}
              totalOccupantsInCell={totalOccupantsInCell}
              cellBedCount={cellBedCount}
              showOccupantDetails={showOccupantDetails}
              onToggleOccupantDetails={() => setShowOccupantDetails((prev) => !prev)}
            />
          </div>
        </>
      ) : (
        <div key={`in-${idx}`} className="rooms-info-cell-inner">
          <BookedCellInner
            booking={b}
            cellDefaultPrice={cellDefaultPrice}
            totalOccupantsInCell={totalOccupantsInCell}
            cellBedCount={cellBedCount}
            showOccupantDetails={showOccupantDetails}
            onToggleOccupantDetails={() => setShowOccupantDetails((prev) => !prev)}
          />
        </div>
      )}
      {count > 1 ? (
        <div
          className="rooms-info-cell-nav-footer"
          onClick={(e) => e.stopPropagation()}
        >
          <button className="rooms-info-cell-nav-btn" onClick={goPrev}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <span
            className={`rooms-info-cell-room-type rooms-info-cell-room-type--${b.allocation_type === "private" ? "private" : "required"}`}
          >
            {b.allocation_type === "private" ? "Private" : "Required"}
          </span>
          <button className="rooms-info-cell-nav-btn" onClick={goNext}>
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      ) : (
        <div className="rooms-info-cell-nav-footer-spacer" aria-hidden="true">
          <span
            className={`rooms-info-cell-room-type rooms-info-cell-room-type--${b.allocation_type === "private" ? "private" : "required"}`}
          >
            {b.allocation_type === "private" ? "Private" : "Required"}
          </span>
        </div>
      )}
    </div>
  );
};
